import * as React from "react";
import { cn } from "@/lib/utils";
import { FloatingInput, type FloatingInputProps } from "./base/floating-input";
import { FloatingLabel } from "./base/floating-label";

export type FloatingLabelInputProps = FloatingInputProps & {
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  label?: string;
  error?: boolean;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  fieldsetClassName?: string;
};

const FloatingLabelInput = React.forwardRef<
  React.ComponentRef<typeof FloatingInput>,
  React.PropsWithoutRef<FloatingLabelInputProps>
>(
  (
    {
      id,
      label,
      error = false,
      className,
      containerProps,
      placeholder,
      value,
      endAdornment,
      startAdornment,
      fieldsetClassName,
      ...props
    },
    ref,
  ) => {
    const effectivePlaceholder = placeholder ?? " ";

    return (
      <div
        {...containerProps}
        className={cn(
          "relative w-full",
          error &&
            "*:text-error [&>fieldset]:border-error [&>fieldset]:dark:border-error",
          containerProps?.className,
        )}
      >
        {startAdornment && (
          <div className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
            {startAdornment}
          </div>
        )}
        <FloatingInput
          ref={ref}
          id={id}
          className={cn(
            className,
            "focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-opacity-0",
            {
              "text-error": error,
            },
            // Adjust padding based on adornments
            startAdornment && "ps-10",
            endAdornment && "pe-10",
          )}
          placeholder={effectivePlaceholder}
          value={value}
          {...props}
        />
        <FloatingLabel
          htmlFor={id}
          size={props.size}
          className={cn(
            "peer-focus:text-primary font-medium m-0",
            {
              "text-error peer-focus:text-error": error,
            },
            // Adjust label position when start adornment exists
            startAdornment && "peer-placeholder-shown:start-10",
          )}
        >
          {label}
        </FloatingLabel>
        <fieldset
          className={cn(
            "absolute peer-focus-visible:border transition-all peer-focus-visible:border-primary inset-0 -top-[5px] border border-stroke-light rounded-xs m-0 py-0 text-right px-2 pointer-events-none select-none min-w-0 peer-focus:[&>legend]:max-w-full peer-focus-visible:[&>legend]:max-w-full peer-placeholder-shown:[&>legend]:max-w-0",
            fieldsetClassName,
          )}
        >
          <legend className="transition-all invisible whitespace-nowrap overflow-hidden w-auto max-w-full h-3 leading-4 text-xs font-normal p-0">
            <span className="px-1 visible inline-block opacity-0">{label}</span>
          </legend>
        </fieldset>
        {endAdornment && (
          <div className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
            {endAdornment}
          </div>
        )}
      </div>
    );
  },
);
FloatingLabelInput.displayName = "FloatingLabelInput";

export { FloatingLabelInput };
